import { importProvidersFrom, inject, InjectionToken, Injector } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  defaultUrlMatcher,
  NavigationEnd,
  NavigationSkipped,
  Route,
  Router,
  RouterStateSnapshot,
  Routes,
  UrlSegment,
  UrlSegmentGroup
} from '@angular/router';
import { PayUModule } from '@simlab/payu';
import { AssetFacade } from '@simOn/asset';
import { NavBarDataInterface, NavBarDataInterfaceI } from '@simOn/common/containers';
import { DevicesFacade, WidgetFacade } from '@simOn/device';
import { DEVICE_MANAGER_PATH } from '@simOn/device/widget/tokens';
import { FullWidthLayoutComponent, SimPageTemplateComponent } from '@simOn/layout';
import { newsManagementGuard } from '@simOn/news/management/guard';
import { PrivilegesFacade } from '@simOn/privileges';
import { ProviderStore } from '@simOn/provider/state';
import { PublicLinkService } from '@simOn/public-link';
import { RoomFacade } from '@simOn/room';
import { SceneFacade } from '@simOn/scene';
import { SpaceFacade } from '@simOn/space';
import { SimSubscriptionControllerService } from '@simOn/subscription';
import { PayUConfig } from '@simOn/subscription/payment-method/tokens';
import { spaceLimitGuard } from '@simOn/subscription/space-limit/guards';
import { SubscriptionInfoService } from '@simOn/subscription/space-limit/services';
import { TicketFacade } from '@simOn/ticket';
import { UserPreferencesFacade } from '@simOn/user';
import { UsersFacade } from '@simOn/user/last-visited/state';
import { ZonesStore } from '@simOn/zone';
import { EventDispatcher } from '@simOn/utils/events';
import {
  autoLoginPartialRoutesGuard,
  AutoLoginPartialRoutesGuard,
  OidcSecurityService,
  UserDataResult
} from 'angular-auth-oidc-client';
import { SidenavLayoutComponent } from 'feature-right-sidenav-layout';
import { combineLatest, filter, firstValueFrom, iif, map, Observable, of, switchMap, takeUntil, tap } from 'rxjs';
import { SimDeviceWarningModalService } from 'simon-application';
import { Environment } from '../environments';
import { CALENDAR_PAGE_DATA } from './calendar.const';
import { DASHBOARD_PAGE_DATA_WITH_PRIVILEGES } from './dashboard.const';
import { DOCUMENTS_PAGE_DATA } from './documents.const';
import { STAGES_PAGE_DATA } from './stages.const';
import { ErrorComponent } from './error/error.component';
import { AuthGuard } from './main/modules/core/services/auth_old/auth.guard';
import { ApartmentAccessGuard } from './main/modules/core/services/guards/apartment-access.guard';
import { LayoutPageRouterOutletComponent } from './pages/layout-page-router/layout-page-router-outlet.component';
import { PROPERTY_SETTINGS_PAGE_DATA_WITH_PRIVILEGES } from './property-settings.const';
import { PROVIDERS_SETTINGS_PAGE_DATA_WITH_PRIVILEGES } from './providers-settings.const';
import { apartmentResolverFn } from './resolvers/apartment.resolver';
import { languageResolver } from './resolvers/language.resolver';
import { spaceInitialViewGuardFn } from './shared/guards/apartment-initial-view.guard';
import { publicLinkDashboardTabGuardFn } from './shared/guards/public-link-dashboard-tab.guard';
import { publicLinkSpaceSectionGuardFn } from './shared/guards/public-link-space-section.guard';
import { connectionsAccessGuardFn } from './shared/guards/connections-access.guard';
import { lastVisitedDashboardTabPath, rememberDashboardTabGuardFn } from './shared/guards/dashboard-last-tab.guard';

export const DeviceErrorListener = new InjectionToken<Observable<unknown>>('Device Error Listener');
export const appRoutes = (environment: Environment): Routes => [
  {
    path: 'public',
    loadChildren: () => import('public-link').then((m) => m.publicLinkRoutes)
  },
  {
    // New (JWT) public links; legacy links above keep working unchanged.
    path: 'public-link',
    loadChildren: () => import('public-link-v2').then((m) => m.publicLinkV2Routes)
  },
  { path: 'callback', loadChildren: () => import('callback').then((m) => m.callbackRoutes) },
  {
    path: 'invitation',
    loadChildren: () => import('invitation').then((m) => m.invitationRoutes),
    canActivate: [
      (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
        const guard = inject(AutoLoginPartialRoutesGuard);
        return guard.canActivate(route, state).pipe(
          tap((canActive) => {
            if (!canActive) {
              const url = state.url;
              url && localStorage.setItem('redirectUri', url);
            }
          })
        );
      }
    ]
  },
  {
    path: 'fibaroAuth',
    loadChildren: () => import('fibaroAuth').then((m) => m.fibaroAuthRoutes)
  },
  {
    path: 'smartThingsAuth',
    loadChildren: () => import('smartThingsAuth').then((m) => m.smartThingsAuthRoutes)
  },
  {
    path: 'wiserHomeAuth',
    canActivate: [AutoLoginPartialRoutesGuard],
    loadChildren: () => import('wiserHomeAuth').then((m) => m.wiserHomeAuthRoutes)
  },
  {
    path: 'netxAuth',
    canActivate: [AutoLoginPartialRoutesGuard],
    loadChildren: () => import('netxAuth').then((m) => m.netxAuthRoutes)
  },
  {
    path: 'maximoAuth',
    canActivate: [AutoLoginPartialRoutesGuard],
    loadChildren: () => import('maximoAuth').then((m) => m.maximoAuthRoutes)
  },
  {
    path: 'SEAuth',
    loadChildren: () => import('SEAuth').then((m) => m.sEAuthRoutes)
  },
  {
    path: 'homeyAuthCallback',
    loadChildren: () => import('homeyAuth').then((m) => m.homeyAuthRoutes)
  },
  {
    path: 'eula',
    loadChildren: () => import('eula').then((m) => m.eulaRoutes),
    canActivate: [AutoLoginPartialRoutesGuard]
  },
  {
    path: 'no-access',
    loadChildren: () => import('no-access').then((m) => m.noAccessRoutes)
  },

  {
    path: 'autologin',
    loadChildren: () => import('autologin').then((m) => m.autologinRoutes)
  },
  { path: 'callback', loadChildren: () => import('callback').then((m) => m.callbackRoutes) },
  {
    path: 'callbackMatterport',
    loadChildren: () => import('callbackMatterport').then((m) => m.callbackMatterportRoutes)
  },
  {
    path: 'payment-summary',
    loadChildren: () => import('payment-summary').then((m) => m.paymentSummaryRoutes)
  },
  {
    path: 'permission-conflict',
    loadChildren: () => import('permission-conflict').then((c) => c.permissionConflictRoutes)
  },
  {
    path: '',
    loadChildren: () => import('feature-overlay-layout').then((m) => m.overlayRoutes),
    resolve: {
      language: languageResolver,
      loadApplication: () => {
        const usersFacade = inject(UsersFacade);
        const oidcSecurityService: OidcSecurityService = inject(OidcSecurityService);
        const userPreferencesFacade = inject(UserPreferencesFacade);
        return oidcSecurityService.userData$.pipe(
          tap((state: UserDataResult) => {
            state.userData && userPreferencesFacade.loadUserPreferences();
            state.userData && usersFacade.setSelectedUserId(state.userData.sub);
          })
        );
      }
    },
    canActivate: [autoLoginPartialRoutesGuard, AuthGuard]
  },
  {
    path: '',
    component: SimPageTemplateComponent,
    children: [
      {
        path: 'property-creation',
        loadChildren: () => import('space-creator').then((m) => m.spaceCreatorRoutes),
        providers: [SubscriptionInfoService],
        canActivate: [AuthGuard, spaceLimitGuard]
      },
      {
        path: 'subscriptions',
        loadChildren: () => import('subscription').then((m) => m.subscriptionRoutes),
        providers: [
          importProvidersFrom(
            PayUModule.forRoot({ ...environment.payment.payU, posId: environment.payment.payU.posId.EURO })
          ),
          SimSubscriptionControllerService,
          {
            provide: PayUConfig,
            useValue: environment.payment.payU
          }
        ],
        canActivate: [AuthGuard]
      },
      {
        path: '',
        redirectTo: 'split-view',
        pathMatch: 'full'
      }
    ],
    resolve: {
      language: languageResolver,
      loadApplication: () => {
        const usersFacade = inject(UsersFacade);
        const oidcSecurityService: OidcSecurityService = inject(OidcSecurityService);
        const userPreferencesFacade = inject(UserPreferencesFacade);
        return oidcSecurityService.userData$.pipe(
          tap((state: UserDataResult) => {
            state.userData && userPreferencesFacade.loadUserPreferences();
            state.userData && usersFacade.setSelectedUserId(state.userData.sub);
          })
        );
      }
    },
    canActivate: [autoLoginPartialRoutesGuard, AuthGuard]
  },
  {
    path: 'cms',
    component: SidenavLayoutComponent,
    canMatch: [],
    children: [
      {
        path: '',
        component: FullWidthLayoutComponent,
        children: [
          {
            path: 'news',
            loadComponent: () => import('news').then((m) => m.NewsManagerComponent)
          }
        ]
      }
    ],
    canActivate: [newsManagementGuard, AutoLoginPartialRoutesGuard]
  },
  {
    path: 'space/:apartmentId',
    component: SidenavLayoutComponent,
    // public link visitors may only open the sections allowlisted in isSpaceSectionAllowed
    canActivateChild: [publicLinkSpaceSectionGuardFn],
    canMatch: [
      (route: Route, segments: UrlSegment[]) => {
        const guard = inject(OidcSecurityService);
        const spaceFacade = inject(SpaceFacade);
        const providerStore = inject(ProviderStore);
        const publicLink = inject(PublicLinkService);
        return publicLink.publicLink$.pipe(
          switchMap((publicLink) => (!!publicLink ? of(!!publicLink) : guard.isAuthenticated())),
          map(async (isActivated: boolean) => {
            if (!isActivated) {
              const redirectUrl = location.pathname.replace(`/${$localize.locale}`, '');
              redirectUrl && localStorage.setItem('redirectUri', redirectUrl);
              guard.authorize();
            }
            const matcher = route.matcher || defaultUrlMatcher;
            const matchResult = matcher(segments, new UrlSegmentGroup(segments, {}), route);
            const apartmentId = matchResult.posParams.apartmentId.path;
            // Subscribe BEFORE dispatching so a fast GetApartmentSuccess can't be
            // emitted before we are listening (which previously left this guard
            // waiting forever and broke the NavBar / left menu).
            const apartmentLoaded = firstValueFrom(
              spaceFacade.apartmentLoaded$.pipe(
                tap(({ apartment }) => {
                  providerStore.loadAllProviders(apartment.providerConnections);
                })
              )
            );
            spaceFacade.setSelectedId(apartmentId);
            await apartmentLoaded;
            return true;
          })
        );
      }
    ],

    children: [
      {
        path: 'apartment',
        canActivate: [ApartmentAccessGuard, spaceInitialViewGuardFn],
        loadChildren: () => import('matterport-viewer').then((m) => m.matterportViewerRoutes)
      },
      {
        path: 'calendar',
        component: FullWidthLayoutComponent,
        data: {
          data: CALENDAR_PAGE_DATA
        } satisfies NavBarDataInterfaceI<NavBarDataInterface>,
        canActivate: [AuthGuard, ApartmentAccessGuard],
        loadChildren: () => import('calendar').then((m) => m.calendarRoutes)
      },
      {
        path: 'interior-designer',
        loadChildren: () => import('interior-designer').then((m) => m.interiorDesignerRoutes)
      },
      {
        path: 'property-settings',
        component: LayoutPageRouterOutletComponent,

        data: {
          tokens: [PrivilegesFacade, PublicLinkService],
          data: PROPERTY_SETTINGS_PAGE_DATA_WITH_PRIVILEGES
        } as NavBarDataInterfaceI<NavBarDataInterface>,
        children: [
          {
            path: 'general-informations',
            loadChildren: () => import('general-information').then((m) => m.generalInformationRoutes)
          },
          {
            path: 'rooms',
            loadChildren: () => import('rooms').then((m) => m.roomsRoutes)
          },
          {
            path: 'asset-categories',
            loadChildren: () => import('asset-categories').then((m) => m.assetCategoriesRoutes)
          },
          {
            path: 'users',
            loadChildren: () => import('users').then((m) => m.usersRoutes)
          },
          {
            path: 'role-templates',
            loadChildren: () => import('role-templates').then((m) => m.roleTemplatesRoutes)
          },
          {
            path: 'activity-log',
            loadChildren: () => import('activity-log').then((m) => m.activityLogRoutes)
          },
          {
            path: 'importer',
            data: {
              hideBottomToolbar: true
            },
            children: [
              {
                path: 'tag',
                loadChildren: () => import('import-mattertag').then((m) => m.importMattertagRoutes)
              },
              {
                path: 'note',
                loadChildren: () => import('import-notes').then((m) => m.importNotesRoutes)
              }
            ]
          },
          { path: '', redirectTo: 'general-informations', pathMatch: 'full' }
        ]
      },
      {
        path: 'providers-settings',
        component: LayoutPageRouterOutletComponent,
        data: {
          tokens: [PrivilegesFacade],
          data: PROVIDERS_SETTINGS_PAGE_DATA_WITH_PRIVILEGES
        } as NavBarDataInterfaceI<NavBarDataInterface>,
        children: [
          {
            path: 'connections',
            loadChildren: () => import('connections').then((m) => m.connectionsRoutes),
            canActivate: [connectionsAccessGuardFn]
          },
          {
            path: 'import-new-device',
            loadChildren: () => import('import-device').then((m) => m.importDeviceRoutes),
            canActivate: [connectionsAccessGuardFn]
          },
          {
            path: 'devices-list',
            loadChildren: () => import('device-list').then((m) => m.deviceListRoutes),
            canActivate: [connectionsAccessGuardFn]
          },
          {
            path: '',
            redirectTo: 'connections',
            pathMatch: 'full'
          }
        ]
      },
      {
        path: 'dashboard',
        component: FullWidthLayoutComponent,
        data: {
          tokens: [ProviderStore, PublicLinkService, Injector],
          data: DASHBOARD_PAGE_DATA_WITH_PRIVILEGES
        } satisfies NavBarDataInterfaceI<NavBarDataInterface>,
        canActivate: [AuthGuard, ApartmentAccessGuard],
        canActivateChild: [rememberDashboardTabGuardFn],
        providers: [{ provide: DEVICE_MANAGER_PATH, useValue: '../../dashboard/devices/deviceManager' }],
        children: [
          {
            path: 'assets',
            loadChildren: () => import('assets').then((m) => m.assetsRoutes)
          },
          {
            path: 'events',
            loadChildren: () => import('events-page').then((m) => m.eventsRoutes),
            canActivate: [publicLinkDashboardTabGuardFn]
          },
          {
            path: 'tickets',
            redirectTo: 'tasks',
            pathMatch: 'full'
          },
          {
            path: 'tasks',
            loadChildren: () => import('tasks').then((m) => m.tasksRoutes),
            canActivate: [publicLinkDashboardTabGuardFn]
          },
          {
            path: 'devices',
            loadChildren: () => import('devices').then((m) => m.deviceRoutes()),
            canActivate: [publicLinkDashboardTabGuardFn]
          },
          {
            path: 'scenes',
            loadChildren: () => import('scenes').then((m) => m.sceneRoutes),
            canActivate: [publicLinkDashboardTabGuardFn]
          },
          {
            path: 'resources',
            loadComponent: () => import('resources').then((m) => m.ResourcesComponent),
            canActivate: [
              publicLinkDashboardTabGuardFn,
              (route: ActivatedRouteSnapshot) => {
                const isMaximoConnected = inject(ProviderStore).isMaximoConnected();
                const router = inject(Router);
                const apartmentId = route.params['id'] || route.params['apartmentId'];
                return of(isMaximoConnected).pipe(
                  map((isMaximoConnected) => {
                    if (!isMaximoConnected) router.navigate([`space/${apartmentId}/providers-settings/connections`]);
                    return isMaximoConnected;
                  })
                );
              }
            ],
            children: [
              {
                path: 'maximo-links',
                loadChildren: () => import('maximo-link').then((m) => m.maximoLinkRoutes)
              }
            ]
          },
          {
            path: '',
            redirectTo: () => lastVisitedDashboardTabPath(),
            pathMatch: 'full'
          }
        ]
      },
      {
        path: 'stages',
        component: FullWidthLayoutComponent,
        data: {
          data: STAGES_PAGE_DATA
        } satisfies NavBarDataInterfaceI<NavBarDataInterface>,
        canActivate: [AuthGuard, ApartmentAccessGuard],
        loadChildren: () => import('stages').then((m) => m.stagesRoutes)
      },
      {
        path: 'documents',
        component: FullWidthLayoutComponent,
        data: {
          data: DOCUMENTS_PAGE_DATA
        } satisfies NavBarDataInterfaceI<NavBarDataInterface>,
        loadChildren: () => import('documents').then((m) => m.spaceDocumentsRoutes)
      },
      {
        path: '',
        redirectTo: 'apartment',
        pathMatch: 'full'
      }
    ],
    providers: [
      ProviderStore,
      SimDeviceWarningModalService,
      {
        provide: DeviceErrorListener,
        deps: [WidgetFacade, SimDeviceWarningModalService, RoomFacade],
        useFactory: (
          widgetFacade: WidgetFacade,
          simDeviceWarningModalService: SimDeviceWarningModalService,
          roomFacade: RoomFacade
        ) => {
          return combineLatest([widgetFacade.checkDevicesAlarmStatus$, roomFacade.rooms$]).pipe(
            tap(([{ devices }, rooms]) => simDeviceWarningModalService.filterDevicesAndOpenModal(devices, rooms)),
            takeUntil(widgetFacade.clearAction)
          );
        }
      }
    ],
    resolve: {
      apartmentResolver: apartmentResolverFn,
      spaceEvents: () => new EventDispatcher(),
      language: languageResolver,
      loadApplication: () => {
        const usersFacade = inject(UsersFacade);
        const oidcSecurityService: OidcSecurityService = inject(OidcSecurityService);
        const userPreferencesFacade = inject(UserPreferencesFacade);
        return oidcSecurityService.userData$.pipe(
          tap((state: UserDataResult) => {
            state.userData && userPreferencesFacade.loadUserPreferences();
            state.userData && usersFacade.setSelectedUserId(state.userData.sub);
          })
        );
      }
    },
    canActivate: [
      (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
        const privilegesFacade = inject(PrivilegesFacade);
        privilegesFacade.loadPrivileges();

        const guard = inject(AutoLoginPartialRoutesGuard);
        const publicLink = inject(PublicLinkService);

        return iif(
          () => publicLink.isPublicLinkModeActive(),
          of(true),
          guard.canActivate(route, state).pipe(
            tap((canActive) => {
              if (!canActive) {
                const url = state.url;
                url && localStorage.setItem('redirectUri', url);
              }
            })
          )
        );
      },
      AuthGuard
    ],
    canDeactivate: [
      (router: SidenavLayoutComponent) => {
        const spaceFacade = inject(SpaceFacade);
        const devicesFacade = inject(DevicesFacade);
        const usersFacade = inject(UsersFacade);
        const widgetFacade = inject(WidgetFacade);
        const assetFacade = inject(AssetFacade);
        const sceneFacade = inject(SceneFacade);
        const roomFacade = inject(RoomFacade);
        const ticketFacade = inject(TicketFacade);
        const privilegesFacade = inject(PrivilegesFacade);
        const zonesStore = inject(ZonesStore);
        firstValueFrom(
          router.routerEvent.pipe(
            filter((event) => event instanceof NavigationEnd || event instanceof NavigationSkipped),
            tap(() => {
              spaceFacade.leaveApartment();
              devicesFacade.clearState();
              ticketFacade.clearState();
              roomFacade.clearState();
              sceneFacade.clearState();
              assetFacade.clearState();
              widgetFacade.clearState();
              usersFacade.clearState();
              spaceFacade.clearState();
              privilegesFacade.clearState();
              zonesStore.clearState();
            })
          )
        );
      }
    ]
  },
  { path: 'unauthorized', redirectTo: '' },
  { path: 'error', component: ErrorComponent },

  { path: '**', redirectTo: '', pathMatch: 'full' }
];
